import React, { useState, useRef, useEffect } from 'react'

// Node Modules
import { useTranslation } from 'react-i18next'
import BCarousel from 'react-bootstrap/Carousel'

// Component
import DocumentImage from 'components/DocumentImage'
import { PreviousIcon, NextIcon, MaximizeViewIcon, CloseIcon, FoldIcon, UnfoldIcon } from 'components/SASIcon'

// Styles
import styles from './Carousel.module.scss'

//Services
import * as service from './Carousel.service'

//Hooks
import { useIsSmallDevice } from 'hooks'

export interface Item {
  src: string
  alt: string
  title: React.ReactNode
  content: React.ReactNode
}

interface Props {
  carouselId?: string
  noWrap?: boolean 
  items: Item[]
}

const Carousel = ({ carouselId, noWrap, items }:Props) => {
  const isMountRef = useRef(true)
  const maximizedFirstEl = useRef<HTMLButtonElement>(null)
  const maximizedLastEl = useRef<HTMLButtonElement>(null)
  const maximizeButtonEl = useRef<HTMLButtonElement>(null)

  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  const [maximized, setMaximized] = useState(false)
  const [showCaption, setShowCaption] = useState(false)
  const [largeImages, setLargeImages] = useState(items.map(i => i.src))

  const isSmallDevice = useIsSmallDevice()

  useEffect(() => {
    Promise.all(items.map(item => service.largeImage(item.src))).then(images => {
      setLargeImages(images)
    })
  }, [items])

  useEffect(() => {
    if (isMountRef.current === true) {
      // Skip initial mount
      isMountRef.current = false
      return
    }

    if (maximized === true) {
      maximizedFirstEl && maximizedFirstEl.current && maximizedFirstEl.current.focus()
    } else {
      maximizeButtonEl && maximizeButtonEl.current && maximizeButtonEl.current.focus()
    }
  }, [maximized])

  const { title, content } = items[index]

  const visualIndex = index + 1

  const caption = (
    <BCarousel.Caption className={styles.caption}>
      <div className={styles.title}>{ title }</div>
      <div className={styles.content}>{ content }</div>
    </BCarousel.Caption>
  )

  const carousel = (
    <BCarousel
      id={carouselId}
      wrap={!noWrap}
      className={styles.slideshow} 
      activeIndex={index}
      onSelect={setIndex}
      interval={null} 
      prevIcon={<div className={styles.prevIcon} title={t('helpcenter-gui-icu.content.carousel.prev_button.title')}><PreviousIcon /></div>}
      prevLabel={t('helpcenter-gui-icu.content.carousel.prev_button.aria')}
      nextIcon={<div className={styles.nextIcon} title={t('helpcenter-gui-icu.content.carousel.next_button.title')}><NextIcon /></div>}
      nextLabel={t('helpcenter-gui-icu.content.carousel.next_button.aria')}
      slide={false}
    >
      {
        items.map((item, i) => <BCarousel.Item key={i}>{maximized ? <span className={styles.vertAlignImgHelper}></span> : null}<DocumentImage src={maximized ? largeImages[i] : item.src} alt={item.alt} /></BCarousel.Item>)
      }
    </BCarousel>
  )

  function handleKeydown(event: React.KeyboardEvent) {
    switch (event.keyCode) {
      case 27:
        // Escape
        event.stopPropagation()
        event.preventDefault()
        setMaximized(false)
        break;
      case 37:
        event.stopPropagation()
        event.preventDefault()
        // Arrow Left
        if (index === 0 && noWrap) {
          return
        } else if (index === 0) {
          setIndex(items.length - 1)
        } else {
          setIndex(index - 1)
        }
        break;
      case 39:
        event.stopPropagation()
        event.preventDefault()
        // Arrow Right
        if (index + 1 === items.length && noWrap) {
          return
        } else if (index + 1 === items.length) {
          setIndex(0)
        } else {
          setIndex(index + 1)
        }
        break; 
    }
  }

  function handleFullscreenTabstopEnd(event: React.KeyboardEvent) {
    if (event.keyCode === 9 && !event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      maximizedFirstEl && maximizedFirstEl.current && maximizedFirstEl.current.focus()
    }
  }

  function handleFullscreenTabstopStart(event: React.KeyboardEvent) {
    if (event.keyCode === 9 && event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      maximizedLastEl && maximizedLastEl.current && maximizedLastEl.current.focus()
    }
  }

  return (
    <div className={`${styles.carousel} ${ maximized && styles.maximized} ${isSmallDevice ? styles.mobile : ''}`} onKeyDown={handleKeydown} role="region" aria-label={t('helpcenter-gui-icu.content.carousel.region.aria')}>
      <span className="sr-only" aria-live="polite">{t('helpcenter-gui-icu.content.carousel.current_slide.aria', { current: visualIndex, total: items.length})}</span>
      {
        maximized ?
        (       
          <>   
            <div className={styles.header}>
              <div className={styles.slideCount}>{visualIndex} / {items.length}</div>
              <button
                ref={maximizedFirstEl}
                onKeyDown={handleFullscreenTabstopStart}
                className={styles.closeButton}
                onClick={() => setMaximized(false)}
                aria-label={t('helpcenter-gui-icu.content.carousel.fullscreen_toggle_button.aria')}
                title={t('helpcenter-gui-icu.content.carousel.fullscreen_toggle_button.label')}
              >
                <CloseIcon />
              </button>
            </div>
            
            { carousel }
            
            <div className={styles.maximizedCaption} aria-live="polite">
              { showCaption && caption }
              <button 
                onKeyDown={handleFullscreenTabstopEnd} 
                ref={maximizedLastEl} 
                className={styles.toggleCaption} 
                onClick={() => setShowCaption(!showCaption)}
                aria-label={showCaption ? t('helpcenter-gui-icu.content.carousel.fullscreen_hide_caption_button.label') : t('helpcenter-gui-icu.content.carousel.fullscreen_show_caption_button.label')}
              >
                {
                  showCaption ?
                    <>{t('helpcenter-gui-icu.content.carousel.fullscreen_hide_caption_button.label')} <UnfoldIcon /></> :
                    <>{t('helpcenter-gui-icu.content.carousel.fullscreen_show_caption_button.label')} <FoldIcon /></>
                }
              </button>
            </div>
          </>
        ) 
        :
        (
          <>
            <button 
              ref={maximizeButtonEl}
              className={styles.fullscreenButton} 
              onClick={() => setMaximized(true)}
              aria-label={t('helpcenter-gui-icu.content.carousel.fullscreen_toggle_button.aria')}
              title={t('helpcenter-gui-icu.content.carousel.fullscreen_toggle_button.label')}
            >
              <MaximizeViewIcon />
            </button> 

            { carousel }

            { caption }
          </>
        )
      }
    </div>
  )
}

export default Carousel